import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getApiService, onUnauthorized } from "../services/api";

const TOKEN_KEY = "kan_token";
const PHONE_KEY = "kan_phone";

interface AuthContextValue {
  token: string | null;
  phone: string | null;
  isAuthed: boolean;
  setAuth: (token: string, phone: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) getApiService(t); // 刷新页面后同步 token 到 API 单例
    return t;
  });
  const [phone, setPhone] = useState<string | null>(() => localStorage.getItem(PHONE_KEY));

  // 接口返回 401（token 过期/被顶下线）时自动登出，由 Router 重定向到登录页
  useEffect(() => {
    onUnauthorized(() => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PHONE_KEY);
      getApiService().updateToken(null);
      setToken(null);
      setPhone(null);
    });
    return () => onUnauthorized(null);
  }, []);

  const setAuth = useCallback((newToken: string, newPhone: string) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(PHONE_KEY, newPhone);
    getApiService(newToken);
    setToken(newToken);
    setPhone(newPhone);
  }, []);

  const logout = useCallback(async () => {
    try {
      await getApiService().logout();
    } catch {
      // 忽略登出接口异常，本地照常清除
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PHONE_KEY);
    getApiService().updateToken(null);
    setToken(null);
    setPhone(null);
  }, []);

  const value = useMemo(
    () => ({ token, phone, isAuthed: !!token, setAuth, logout }),
    [token, phone, setAuth, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
